/**
 * VAT Toggle Module
 * Handles switching between VAT-inclusive and VAT-exclusive price display
 *
 * Features:
 * - First-time visitor popup
 * - localStorage + cookie preference storage
 * - Client-side price calculation
 * - Instant price updates without page reload
 * - Integration with cart AJAX updates
 */

// Storage keys
const STORAGE_KEY = 'vatPreference';
const COOKIE_NAME = 'vat_preference';

// VAT modes
const VAT_MODES = {
  INCL: 'incl',
  EXCL: 'excl'
};

// Price selectors - all elements that display prices
const PRICE_SELECTORS = [
  '.price-item.price-item--regular',
  '.price-item.price-item--sale',
  '.price-item--last',
  '.cart-item__old-price',
  '.cart-item__final-price',
  '.totals__total-value',
  'div.vat-price',
  '.price-per-item--current',
  '.variant-item__old-price',
  '.price--end .price-item',
  '.unit-price .price-item'
].join(', ');

// ===== STATE MANAGEMENT =====

class VATToggleState {
  constructor() {
    this.mode = this.loadPreference();
    this.hasSeenModal = this.checkHasSeenModal();
  }

  loadPreference() {
    // Try localStorage first
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data.vatMode || VAT_MODES.INCL;
      }
    } catch (e) {
      console.warn('[VAT Toggle] localStorage not available:', e);
    }

    // Fallback to cookie
    const cookieValue = this.getCookie(COOKIE_NAME);
    return cookieValue || VAT_MODES.INCL;
  }

  savePreference(mode) {
    this.mode = mode;

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        vatMode: mode,
        lastUpdated: Date.now(),
        hasSeenModal: true
      }));
    } catch (e) {
      console.warn('[VAT Toggle] Could not save to localStorage:', e);
    }

    // Save to cookie
    this.setCookie(COOKIE_NAME, mode, 365);
  }

  checkHasSeenModal() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data.hasSeenModal || false;
      }
    } catch (e) {
      // Ignore
    }

    // Also check cookie as fallback
    return !!this.getCookie(COOKIE_NAME);
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax; Secure`;
  }
}

// ===== PRICE CALCULATOR =====

class PriceCalculator {
  constructor() {
    this.config = window.shopifyThemeConfig || {};
    this.vatRate = parseFloat(this.config.vatRate) || 0.09;
  }

  calculatePrice(basePrice, mode, customVatRate) {
    const rate = customVatRate !== undefined ? customVatRate : this.vatRate;

    // Base price is already in cents, stored as excl. VAT
    if (mode === VAT_MODES.INCL) {
      return Math.round(basePrice * (1 + rate));
    }
    return basePrice;
  }

  formatMoney(cents) {
    const format = this.config.currencyCodeEnabled
      ? this.config.moneyWithCurrencyFormat
      : this.config.moneyFormat;

    if (!format) {
      // Fallback formatting
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
    }

    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100);
    const amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithComma);
  }
}

// ===== PRICE UPDATER =====

class PriceUpdater {
  constructor(calculator) {
    this.calculator = calculator;
  }

  updateAllPrices(mode) {
    const elements = document.querySelectorAll(PRICE_SELECTORS);

    elements.forEach(element => {
      this.updatePriceElement(element, mode);
    });
  }

  updatePriceElement(element, mode) {
    if (!element.dataset.priceBase) return;

    const basePrice = parseFloat(element.dataset.priceBase);
    if (isNaN(basePrice)) return;

    const customVatRate = element.dataset.vatRate ? parseFloat(element.dataset.vatRate) : undefined;

    const newPrice = this.calculator.calculatePrice(basePrice, mode, customVatRate);
    const formattedPrice = this.calculator.formatMoney(newPrice);

    // Get suffix text from data attributes or translations
    const suffix = this.getSuffix(element, mode);

    // Inject suffix directly into the price element
    element.textContent = `${formattedPrice} ${suffix}`;
  }

  getSuffix(element, mode) {
    // Try to find suffix data from nearby suffix element
    let suffixElement = element.nextElementSibling;
    if (!suffixElement || !suffixElement.classList.contains('vat-suffix')) {
      const container = element.closest('.price__container, .price, .product-option');
      if (container) {
        suffixElement = container.querySelector('.vat-suffix');
      }
    }

    if (suffixElement) {
      const suffixText = mode === 'incl'
        ? suffixElement.dataset.suffixIncl
        : suffixElement.dataset.suffixExcl;

      if (suffixText) return suffixText;
    }

    // Fallback to default suffixes
    return mode === 'incl' ? '(incl. BTW)' : '(excl. BTW)';
  }
}

// ===== MODAL CONTROLLER =====

class VATModal {
  constructor(onChoice) {
    this.onChoice = onChoice;
    this.modal = null;
    this.escHandler = null;
  }

  show() {
    this.modal = document.getElementById('vat-modal');
    if (!this.modal) {
      console.warn('[VAT Toggle] Modal element not found');
      return;
    }

    this.modal.classList.add('vat-modal--visible');
    document.body.classList.add('vat-modal-open');

    // Trap focus
    this.trapFocus();

    // Event listeners
    this.modal.querySelectorAll('[data-choice]').forEach(button => {
      button.addEventListener('click', (e) => {
        const choice = e.target.dataset.choice;
        this.hide();
        this.onChoice(choice);
      });
    });

    // ESC key
    this.escHandler = (e) => {
      if (e.key === 'Escape') {
        this.hide();
        this.onChoice(VAT_MODES.INCL); // Default to inclusive
      }
    };
    document.addEventListener('keydown', this.escHandler);

    // Backdrop click
    const overlay = this.modal.querySelector('.vat-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.hide();
        this.onChoice(VAT_MODES.INCL); // Default to inclusive
      });
    }
  }

  hide() {
    if (!this.modal) return;

    this.modal.classList.remove('vat-modal--visible');
    document.body.classList.remove('vat-modal-open');

    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement.focus();

    this.modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    });
  }
}

// ===== TOGGLE BUTTON CUSTOM ELEMENT =====

class VATToggle extends HTMLElement {
  constructor() {
    super();
    this.state = null;
    this.onToggle = null;
  }

  connectedCallback() {
    const button = this.querySelector('.vat-toggle__button');
    if (!button) return;

    button.addEventListener('click', () => {
      if (!this.state) return;

      const currentMode = this.state.mode;
      const newMode = currentMode === VAT_MODES.INCL ? VAT_MODES.EXCL : VAT_MODES.INCL;

      if (this.onToggle) {
        this.onToggle(newMode);
      }
    });
  }

  updateUI(mode) {
    const button = this.querySelector('.vat-toggle__button');
    if (!button) return;

    button.setAttribute('aria-pressed', mode === VAT_MODES.EXCL ? 'true' : 'false');
    button.setAttribute('data-mode', mode);

    // Update parent element data-active for label styling
    this.setAttribute('data-active', mode);

    // Update visual state
    this.querySelectorAll('.vat-toggle__option').forEach(option => {
      const optionMode = option.dataset.state;
      if (optionMode === mode) {
        option.classList.add('vat-toggle__option--active');
      } else {
        option.classList.remove('vat-toggle__option--active');
      }
    });
  }
}

// Register custom element
if (typeof customElements !== 'undefined') {
  customElements.define('vat-toggle', VATToggle);
}

// ===== MAIN APPLICATION =====

class VATToggleApp {
  constructor() {
    this.state = new VATToggleState();
    this.calculator = new PriceCalculator();
    this.updater = new PriceUpdater(this.calculator);
    this.modal = new VATModal((choice) => this.handleModalChoice(choice));

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onReady());
    } else {
      this.onReady();
    }
  }

  onReady() {
    // Show modal if first visit - DISABLED FOR NOW
    // if (!this.state.hasSeenModal) {
    //   setTimeout(() => this.modal.show(), 500);
    // } else {
    //   // Apply saved preference immediately
    //   this.applyVATMode(this.state.mode);
    // }

    // Apply saved preference immediately
    this.applyVATMode(this.state.mode);

    // Setup toggle buttons
    this.setupToggles();

    // Subscribe to cart updates
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => this.applyVATMode(this.state.mode), 100);
      });
    }
  }

  setupToggles() {
    const toggles = document.querySelectorAll('vat-toggle');
    toggles.forEach(toggle => {
      toggle.state = this.state;
      toggle.onToggle = (mode) => this.handleToggle(mode);
      toggle.updateUI(this.state.mode);
    });
  }

  handleModalChoice(choice) {
    this.state.savePreference(choice);
    this.applyVATMode(choice);
  }

  handleToggle(mode) {
    this.state.savePreference(mode);
    this.applyVATMode(mode);

    // Fix scroll lock on mobile: remove overflow-hidden classes
    // These classes are added by the menu drawer and can persist after toggling
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    document.body.style.overflow = '';
  }

  applyVATMode(mode) {
    // Update all prices
    this.updater.updateAllPrices(mode);

    // Update toggle UI
    const toggles = document.querySelectorAll('vat-toggle');
    toggles.forEach(toggle => toggle.updateUI(mode));

    // Publish event for other components
    if (typeof publish === 'function') {
      const eventName = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.vatModeChanged)
        ? PUB_SUB_EVENTS.vatModeChanged
        : 'vatModeChanged';
      publish(eventName, { mode });
    }
  }
}

// Initialize app
new VATToggleApp();
